import React, { useEffect, useMemo, useState } from "react";
import api, { formatMoney, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Receipt, Trash2, TrendingUp, TrendingDown, Pencil } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  type: "sale",
  company_id: "",
  category: "",
  description: "",
  amount: "",
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  status: "pending",
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tab, setTab] = useState("sale");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  async function load() {
    const [t, c] = await Promise.all([
      api.get("/transactions"),
      api.get("/companies"),
    ]);
    setItems(t.data);
    setCompanies(c.data);
  }
  useEffect(() => { load(); }, []);

  const invoices = items.filter((x) => x.type === "sale" || x.type === "purchase");
  const sales = invoices.filter((x) => x.type === "sale");
  const purchases = invoices.filter((x) => x.type === "purchase");

  const totals = useMemo(() => {
    const salesTotal = sales.reduce((a, b) => a + b.amount, 0);
    const purchasesTotal = purchases.reduce((a, b) => a + b.amount, 0);
    return { sales: salesTotal, purchases: purchasesTotal, profit: salesTotal - purchasesTotal };
  }, [sales, purchases]);

  function openNew(type) {
    setEditingId(null);
    setForm({ ...emptyForm, type });
    setOpen(true);
  }

  function openEdit(t) {
    setEditingId(t.id);
    setForm({
      type: t.type,
      company_id: t.company_id || "",
      category: t.category || "",
      description: t.description || "",
      amount: String(t.amount ?? ""),
      issue_date: t.issue_date ? t.issue_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      due_date: t.due_date ? t.due_date.slice(0, 10) : "",
      status: t.status || "pending",
    });
    setOpen(true);
  }

  async function save() {
    try {
      if (!form.description || !form.amount) return toast.error("Zorunlu alanları doldurun");
      const payload = {
        ...form,
        amount: Number(form.amount),
        company_id: form.company_id || null,
        issue_date: new Date(form.issue_date).toISOString(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      };
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Fatura güncellendi");
      } else {
        await api.post("/transactions", payload);
        toast.success("Fatura eklendi");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Fatura silindi");
    await load();
  }

  function renderTable(rows) {
    if (rows.length === 0) return <div className="p-10 text-center text-slate-500">Fatura bulunamadı</div>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02]">
            <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
              <th className="py-3 px-5">Tarih</th>
              <th className="py-3 px-5">Firma</th>
              <th className="py-3 px-5">Kategori</th>
              <th className="py-3 px-5">Açıklama</th>
              <th className="py-3 px-5">Vade</th>
              <th className="py-3 px-5">Tutar</th>
              <th className="py-3 px-5">Durum</th>
              <th className="py-3 px-5 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const c = companies.find((x) => x.id === t.company_id);
              return (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${t.id}`}>
                  <td className="py-3 px-5 tabular text-slate-300">{formatDate(t.issue_date)}</td>
                  <td className="py-3 px-5 font-medium">{c?.name || "-"}</td>
                  <td className="py-3 px-5 text-slate-400">{t.category}</td>
                  <td className="py-3 px-5 text-slate-300">{t.description}</td>
                  <td className="py-3 px-5 tabular text-slate-300">{formatDate(t.due_date)}</td>
                  <td className="py-3 px-5 tabular font-semibold">{formatMoney(t.amount)}</td>
                  <td className="py-3 px-5">
                    {t.status === "paid" ? (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">Ödendi</Badge>
                    ) : t.status === "overdue" ? (
                      <Badge className="bg-red-500/15 text-red-300 border-red-500/25">Gecikti</Badge>
                    ) : (
                      <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/25">Beklemede</Badge>
                    )}
                  </td>
                  <td className="py-3 px-5 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10" data-testid={`edit-invoice-${t.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-invoice-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Fatura Yönetimi</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-amber-400" /> Alış & Satış Faturaları
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Faturalarınızı ekleyin, aylık ve yıllık kâr takibinizi yapın.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openNew("sale")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" data-testid="add-sale-btn">
            <Plus className="w-4 h-4 mr-1" /> Satış Faturası
          </Button>
          <Button onClick={() => openNew("purchase")} className="bg-red-500 hover:bg-red-400 text-white font-semibold" data-testid="add-purchase-btn">
            <Plus className="w-4 h-4 mr-1" /> Alış Faturası
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-elevated rounded-2xl p-5 border border-emerald-500/25">
          <div className="flex items-center gap-2 text-emerald-300 text-xs mono uppercase tracking-widest">
            <TrendingUp className="w-4 h-4" /> Toplam Satış
          </div>
          <div className="text-3xl font-bold tabular mt-2">{formatMoney(totals.sales)}</div>
          <div className="text-xs text-slate-500 mt-1">{sales.length} fatura</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border border-red-500/25">
          <div className="flex items-center gap-2 text-red-300 text-xs mono uppercase tracking-widest">
            <TrendingDown className="w-4 h-4" /> Toplam Alış
          </div>
          <div className="text-3xl font-bold tabular mt-2">{formatMoney(totals.purchases)}</div>
          <div className="text-xs text-slate-500 mt-1">{purchases.length} fatura</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border border-amber-500/40 amber-glow">
          <div className="flex items-center gap-2 text-amber-300 text-xs mono uppercase tracking-widest">Güncel Kâr</div>
          <div className={`text-3xl font-bold tabular mt-2 ${totals.profit >= 0 ? "text-amber-300" : "text-red-300"}`}>
            {formatMoney(totals.profit)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Satış − Alış</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#111723] border border-white/5">
          <TabsTrigger value="sale" data-testid="tab-sale">Satış ({sales.length})</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="tab-purchase">Alış ({purchases.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
        </TabsList>
        <TabsContent value="sale" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderTable(sales)}</div></TabsContent>
        <TabsContent value="purchase" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderTable(purchases)}</div></TabsContent>
        <TabsContent value="all" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderTable(invoices)}</div></TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B111C] border-white/10 max-w-lg" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? (form.type === "sale" ? "Satış Faturası Düzenle" : "Alış Faturası Düzenle") : (form.type === "sale" ? "Satış Faturası Ekle" : "Alış Faturası Ekle")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Firma</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="bg-[#111723] border-white/10 mt-1" data-testid="invoice-company-select"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
                <SelectContent className="bg-[#0B111C] border-white/10 max-h-64">
                  {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kategori</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Örn. Otomasyon Panosu" className="bg-[#111723] border-white/10 mt-1" />
              </div>
              <div>
                <Label>Tutar (₺)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="invoice-amount-input" />
              </div>
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#111723] border-white/10 mt-1" rows={2} data-testid="invoice-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fatura Tarihi</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
              </div>
              <div>
                <Label>Vade Tarihi</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
              </div>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#111723] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B111C] border-white/10">
                  <SelectItem value="pending">Beklemede</SelectItem>
                  <SelectItem value="paid">Ödendi</SelectItem>
                  <SelectItem value="overdue">Gecikti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">İptal</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-invoice-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
